/**
 * The signed contract, and whether we actually hold it.
 *
 * The platform used to store a signature - the strokes, the typed name, the IP, the time
 * - and never the document those things were applied to. This panel is where somebody
 * checks that the contract exists, reads it, and if it is missing, has it produced from
 * the signature that is already on file.
 *
 * Producing it later is not inventing history. The signature, the name, the time and the
 * address it came from are all real and already stored; the PDF is a rendering of them.
 * The panel keeps the two dates apart on purpose - when it was signed, and when the file
 * was written - so nobody can mistake one for the other.
 */
import { useCallback, useEffect, useState } from 'react'
import { Deal, User } from '../../types'
import { apiHeadersOrThrow } from '../../lib/authHeader'
import { ACTIONS, logAction, shortWhen } from '../../services/actionLog'
import './SignedDocumentPanel.css'

interface Props {
  deal: Deal
  user: User
  onSaved: (msg: string) => void
}

interface DocumentState {
  filed: boolean
  path: string | null
  sha256: string | null
  filedAt: string | null
  signedAt: string | null
  signedBy: string | null
  url: string | null
  urlError: string | null
}

export default function SignedDocumentPanel({ deal, user, onSaved }: Props) {
  const [doc, setDoc] = useState<DocumentState | null>(null)
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notSigned, setNotSigned] = useState(false)

  const load = useCallback(
    async (refile: boolean) => {
      setBusy(true)
      setError(null)
      try {
        const res = await fetch('/api/deals/bos-document', {
          method: 'POST',
          headers: await apiHeadersOrThrow(),
          body: JSON.stringify({ dealId: deal.id, refile }),
        })
        const data = await res.json().catch(() => null)

        if (!res.ok || !data || !data.success) {
          if (data && data.code === 'not_signed') {
            setNotSigned(true)
            setDoc(null)
            return
          }
          setError((data && data.error) || 'Could not reach the document store.')
          return
        }

        setNotSigned(false)
        setDoc({
          filed: Boolean(data.filed),
          path: data.path || null,
          sha256: data.sha256 || null,
          filedAt: data.filedAt || null,
          signedAt: data.signedAt || null,
          signedBy: data.signedBy || null,
          url: data.url || null,
          urlError: data.urlError || null,
        })

        /* Only worth an audit line when a file was actually written. Opening the panel is
           not an event; producing the contract is. */
        if (data.filed) {
          void logAction({
            dealId: deal.id,
            action: ACTIONS.BOS_DOCUMENT_FILED,
            actorId: user.id,
            details: { sha256: data.sha256, refile },
          })
          onSaved(
            refile
              ? 'The contract was produced again from the same signature.'
              : 'The signed contract was missing and has been filed.'
          )
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not reach the document store.')
      } finally {
        setBusy(false)
      }
    },
    [deal.id, user.id, onSaved]
  )

  useEffect(() => {
    void load(false)
  }, [load])

  if (notSigned) {
    return (
      <div className="sd-panel">
        <div className="sd-head">
          <h4>Signed contract</h4>
          <p>
            Nobody has signed this deal yet, so there is no contract to keep. It appears here
            the moment the seller signs.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="sd-panel">
      <div className="sd-head">
        <h4>Signed contract</h4>
        <p>
          The Vehicle Purchase Agreement exactly as it was signed. This is the copy we are required to keep,
          and the copy the seller was sent.
        </p>
      </div>

      {busy && !doc ? <p className="sd-quiet">Looking for the document…</p> : null}

      {error ? <div className="sd-alert">{error}</div> : null}

      {doc ? (
        <>
          <div className="sd-rows">
            <div className="sd-row">
              <span className="sd-k">Signed</span>
              <span className="sd-v">
                {doc.signedAt ? shortWhen(doc.signedAt) : 'Not recorded'}
                {doc.signedBy ? ' · ' + doc.signedBy : ''}
              </span>
            </div>
            <div className="sd-row">
              <span className="sd-k">Document filed</span>
              <span className="sd-v">
                {doc.filedAt ? shortWhen(doc.filedAt) : 'Not filed'}
                {doc.filed ? ' · just now' : ''}
              </span>
            </div>
            {/* The fingerprint is what ties "she signed" to "she signed this". */}
            <div className="sd-row">
              <span className="sd-k">Fingerprint</span>
              <span className="sd-v sd-hash">{doc.sha256 || 'Not recorded'}</span>
            </div>
          </div>

          {doc.url ? (
            <a className="sd-open" href={doc.url} target="_blank" rel="noopener noreferrer">
              Open the signed contract
            </a>
          ) : (
            <div className="sd-alert">
              {doc.urlError || 'The document is on file but could not be opened just now.'}
            </div>
          )}

          <div className="sd-actions">
            <button className="sd-refile" onClick={() => load(true)} disabled={busy}>
              {busy ? 'Working…' : 'Produce it again'}
            </button>
            <span className="sd-quiet">
              Produces the same contract from the same signature. Use it if the file will not
              open, or after the company details have changed.
            </span>
          </div>
        </>
      ) : null}
    </div>
  )
}
